import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { setLanguage } from '@/shared/actions';
import useTranslation from '@/shared/hooks/useTranslation';
import { getLanguage } from '@/services/GNRL';
import AuthPageContainer from '../auth/AuthPageContainter';
import { setDeeplinkData } from '../route/actions';
import MessagePanel from './PublicFormApplication/MessagePanel';
import PublicFormSrcIframe from './PublicFormApplication/PublicFormSrcIframe';
import PublicFormVerificationPanel from './PublicFormApplication/PublicFormVerificationPanel';
import { fetchPublicApplicationTypeByLanguage } from './PublicFormApplication/publicFormApplicationApi';

const REDIRECT_DELAY = 7000;

const scheduleHomeRedirect = ({ history, redirectTimerRef }) => {
  if (redirectTimerRef.current) {
    clearTimeout(redirectTimerRef.current);
  }

  redirectTimerRef.current = setTimeout(() => {
    history.push('/');
  }, REDIRECT_DELAY);
};

const PublicFormApplication = ({ history }) => {
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [shouldShowIframe, setShouldShowIframe] = useState(false);
  const [isRefreshingApplicationType, setIsRefreshingApplicationType] =
    useState(false);
  const [iframeError, setIframeError] = useState(null);
  const [iframeSuccess, setIframeSuccess] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(getLanguage() || '');
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const redirectTimerRef = useRef(null);
  const dispatch = useDispatch();
  const translate = useTranslation();

  const deeplinkData = useSelector((state) => state.deeplinkReducer);
  const deeplinkMeta = deeplinkData?.meta;
  const publicApplicationType = deeplinkMeta?.public_application_type || null;
  const publicApplicationTypeFound =
    deeplinkMeta?.public_application_type_found === true;
  const applicationTitle =
    publicApplicationType?.title || publicApplicationType?.name;

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const goHome = () => {
    history.push('/');
  };

  const goToLogin = () => {
    history.push('/hello');
  };

  const handleLanguageChange = async (newLanguage) => {
    setSelectedLanguage(newLanguage);
    setRecaptchaToken(null);
    setRecaptchaKey((currentKey) => currentKey + 1);
    setShouldShowIframe(false);
    setIframeError(null);
    setIframeSuccess(null);

    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }

    const languagePromise = dispatch(setLanguage(newLanguage));

    if (!deeplinkData?.authority || !deeplinkData?.payload_code) {
      await languagePromise;
      return;
    }

    setIsRefreshingApplicationType(true);

    try {
      const [, { publicApplicationTypes, publicApplicationType }] =
        await Promise.all([
          languagePromise,
          fetchPublicApplicationTypeByLanguage({
            authority: deeplinkData.authority,
            language: newLanguage,
            code: deeplinkData.payload_code,
          }),
        ]);

      dispatch(
        setDeeplinkData({
          action: deeplinkData.action,
        payload_code: deeplinkData.payload_code,
        authority: deeplinkData.authority,
        country: deeplinkData.country,
        lang: newLanguage,
        meta: {
            ...deeplinkMeta,
            public_application_types: publicApplicationTypes,
            public_application_types_error: null,
            public_application_type: publicApplicationType || null,
            public_application_type_found: !!publicApplicationType,
          },
          needsDeeplinkRedirection: deeplinkData.needsDeeplinkRedirection,
        })
      );
    } catch (error) {
      dispatch(
        setDeeplinkData({
          action: deeplinkData.action,
        payload_code: deeplinkData.payload_code,
        authority: deeplinkData.authority,
        country: deeplinkData.country,
        lang: newLanguage,
        meta: {
            ...deeplinkMeta,
            public_application_types: [],
            public_application_types_error: true,
            public_application_type: null,
            public_application_type_found: false,
          },
          needsDeeplinkRedirection: deeplinkData.needsDeeplinkRedirection,
        })
      );
    } finally {
      setIsRefreshingApplicationType(false);
    }
  };

  const continueToApplication = async () => {
    if (
      !recaptchaToken ||
      !publicApplicationType?.url ||
      shouldShowIframe ||
      isRefreshingApplicationType
    ) {
      return;
    }

    setIframeError(null);
    setIframeSuccess(null);
    setShouldShowIframe(true);
  };

  const handleIframeMessage = ({ payload }) => {
    if (typeof payload !== 'object') return;

    console.log('Public form postMessage payload:', payload);

    const hasApiCode =
      payload.code !== null && payload.code !== undefined && payload.code !== '';
    const apiTranslationKey = hasApiCode ? `api_${payload.code}` : null;
    const translatedMessage = apiTranslationKey
      ? translate(apiTranslationKey, payload)
      : null;

    if (payload.error === false) {
      setIframeError(null);
      setIframeSuccess(
        translatedMessage && translatedMessage !== apiTranslationKey
          ? translatedMessage
          : translate('OTHER.success')
      );
      setShouldShowIframe(false);
      scheduleHomeRedirect({ history, redirectTimerRef });
      return;
    }

    if (!payload.error) return;

    setIframeSuccess(null);
    setIframeError(
      translatedMessage && translatedMessage !== apiTranslationKey
        ? translatedMessage
        : payload.result ||
            payload.user_description ||
            translate('publicFormApplication.submitError')
    );
    setShouldShowIframe(false);
  };

  const renderContent = () => {
    if (isRefreshingApplicationType) {
      return (
        <MessagePanel
          title={translate('publicFormApplication.loadingTitle')}
          description={translate('publicFormApplication.loadingDescription')}
        />
      );
    }

    if (!publicApplicationTypeFound) {
      return (
        <MessagePanel
          title={translate('publicFormApplication.notFoundTitle')}
          description={translate('publicFormApplication.notFoundDescription')}
          actionText={translate('PublicPaymentsLoginBtn')}
          onAction={goToLogin}
        />
      );
    }

    if (shouldShowIframe) {
      return (
        <PublicFormSrcIframe
          applicationTitle={applicationTitle || publicApplicationType?.name}
          publicApplicationType={publicApplicationType}
          deeplinkMeta={deeplinkMeta}
          recaptchaToken={recaptchaToken}
          onMessage={handleIframeMessage}
        />
      );
    }

    if (iframeError || iframeSuccess) {
      return (
        <MessagePanel
          title={
            iframeError
              ? translate('publicFormApplication.genericErrorTitle')
              : translate('OTHER.success')
          }
          description={iframeError || iframeSuccess}
          actionText={iframeError ? translate('Home') : undefined}
          onAction={iframeError ? goHome : undefined}
        />
      );
    }

    return (
      <PublicFormVerificationPanel
        applicationTitle={applicationTitle}
        isLoading={isRefreshingApplicationType}
        recaptchaToken={recaptchaToken}
        recaptchaKey={recaptchaKey}
        onCancel={goHome}
        onContinue={continueToApplication}
        onVerify={setRecaptchaToken}
      />
    );
  };

  return (
    <AuthPageContainer
      language={selectedLanguage}
      handleLanguageChange={handleLanguageChange}
      fullWidthContent
    >
      {renderContent()}
    </AuthPageContainer>
  );
};

export default withRouter(PublicFormApplication);
